import { useState, useRef } from 'react';
import { Search, CheckCircle, Clock, AlertTriangle, Plus, Filter, Trash2, Upload, FilePlus, Paperclip, UserCheck, X, FileText } from 'lucide-react';
import Header from '../../components/layout/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import StatusBadge, { StatusType } from '../../components/ui/StatusBadge';
import { Textarea } from '../../components/ui/textarea';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious
} from '../../components/ui/pagination';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { useToast } from '../../components/ui/use-toast';
import { motion } from 'framer-motion';

type DocumentStatus = 'pending' | 'approved' | 'rejected';
type OffboardingStatus = 'pending' | 'in-progress' | 'completed' | 'rejected' | 'approval_pending';
type DepartmentType = 'IT' | 'HR' | 'Finance' | 'Admin';

interface OffboardingItem {
  id: string;
  name: string;
  completed: boolean;
  category: DepartmentType;
  documentRequired?: boolean;
  document?: {
    fileName: string;
    uploadDate: string;
    status: DocumentStatus;
    approvalDate?: string;
    approvedBy?: string;
    comment?: string;
  } | null;
}

interface ClearanceSummary {
  ITClearance: boolean;
  HRClearance: boolean;
  FinanceClearance: boolean;
  AdminClearance: boolean;
  overallApproved: boolean;
  approvers: {
    name: string;
    role: string;
    approved: boolean;
    timestamp?: string;
    comments?: string;
  }[];
}

interface OffboardingRecord {
  id: string;
  employeeId: string;
  name: string;
  email: string;
  department: string;
  designation: string;
  exitDate: string;
  status: OffboardingStatus;
  reason: string;
  items: OffboardingItem[];
  clearanceSummary?: ClearanceSummary;
  submittedBy?: string;
  submittedDate?: string;
  completedDate?: string;
}

const generateOffboardingData = (): OffboardingRecord[] => {
  const departments = ['Faculty', 'Administration', 'Technical', 'Marketing', 'HR'];
  const designations = [
    'Python Instructor', 'MERN Stack Instructor', 'Java Instructor', 
    'Office Admin', 'System Administrator', 'Digital Marketing Specialist',
    'HR Executive', 'Course Coordinator', 'Placement Officer'
  ];
  
  const reasons = [
    'Resignation - Better opportunity',
    'Resignation - Personal reasons',
    'Resignation - Higher education',
    'Termination - Poor performance',
    'Termination - Misconduct',
    'Contract expiration',
    'Retirement'
  ];
  
  const offboardingItems: OffboardingItem[] = [
    { id: '1', name: 'Return IT Equipment', completed: false, category: 'IT', documentRequired: true },
    { id: '2', name: 'Revoke System Access', completed: false, category: 'IT' },
    { id: '3', name: 'Disable Email Account', completed: false, category: 'IT' },
    { id: '4', name: 'Exit Interview', completed: false, category: 'HR', documentRequired: true },
    { id: '5', name: 'Return ID Card', completed: false, category: 'Admin' },
    { id: '6', name: 'Clear Dues', completed: false, category: 'Finance', documentRequired: true },
    { id: '7', name: 'Final Settlement', completed: false, category: 'Finance' },
    { id: '8', name: 'Remove from Payroll', completed: false, category: 'Finance' },
    { id: '9', name: 'Knowledge Transfer', completed: false, category: 'HR', documentRequired: true },
  ];
  
  const records: OffboardingRecord[] = [];
  for (let i = 1; i <= 15; i++) {
    const department = departments[Math.floor(Math.random() * departments.length)];
    const designation = designations[Math.floor(Math.random() * designations.length)];
    const reason = reasons[Math.floor(Math.random() * reasons.length)];
    
    const exitDate = new Date();
    exitDate.setDate(exitDate.getDate() + Math.floor(Math.random() * 30) + 1);
    
    let status: OffboardingStatus;
    const random = Math.random();
    
    if (random < 0.3) {
      status = 'pending';
    } else if (random < 0.5) {
      status = 'in-progress';
    } else if (random < 0.7) {
      status = 'approval_pending';
    } else if (random < 0.9) {
      status = 'completed';
    } else {
      status = 'rejected';
    }
    
    const items = offboardingItems.map(item => {
      let completed = false;
      let document = null;
      
      if (status === 'completed') {
        completed = true;
        if (item.documentRequired) {
          document = {
            fileName: `${item.name.toLowerCase().replace(/ /g, '_')}_employee${i}.pdf`,
            uploadDate: new Date(Date.now() - Math.floor(Math.random() * 7 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0],
            status: 'approved' as DocumentStatus,
            approvalDate: new Date().toISOString().split('T')[0],
            approvedBy: 'MASTERADMIN'
          };
        }
      } else if (status === 'approval_pending') {
        completed = item.id <= '6';
        if (item.documentRequired && completed) {
          document = {
            fileName: `${item.name.toLowerCase().replace(/ /g, '_')}_employee${i}.pdf`,
            uploadDate: new Date().toISOString().split('T')[0],
            status: 'pending' as DocumentStatus
          };
        }
      } else if (status === 'in-progress') {
        completed = Math.random() > 0.5;
        if (completed && item.documentRequired) {
          document = {
            fileName: `${item.name.toLowerCase().replace(/ /g, '_')}_employee${i}.pdf`,
            uploadDate: new Date().toISOString().split('T')[0],
            status: Math.random() > 0.5 ? 'approved' as DocumentStatus : 'pending' as DocumentStatus
          };
        }
      }
      
      return { ...item, completed, document };
    });
    
    let clearanceSummary = undefined;
    if (status === 'completed') {
      clearanceSummary = {
        ITClearance: true,
        HRClearance: true,
        FinanceClearance: true,
        AdminClearance: true,
        overallApproved: true,
        approvers: [
          {
            name: 'IT Manager',
            role: 'IT',
            approved: true,
            timestamp: new Date(Date.now() - Math.floor(Math.random() * 3 * 24 * 60 * 60 * 1000)).toISOString()
          },
          {
            name: 'HR Director',
            role: 'HR',
            approved: true,
            timestamp: new Date(Date.now() - Math.floor(Math.random() * 2 * 24 * 60 * 60 * 1000)).toISOString()
          },
          {
            name: 'Finance Manager',
            role: 'Finance',
            approved: true,
            timestamp: new Date(Date.now() - Math.floor(Math.random() * 1 * 24 * 60 * 60 * 1000)).toISOString()
          },
          {
            name: 'Admin Officer',
            role: 'Admin',
            approved: true,
            timestamp: new Date().toISOString()
          }
        ]
      };
    } else if (status === 'approval_pending') {
      const itApproved = Math.random() > 0.5;
      const hrApproved = Math.random() > 0.5;
      const financeApproved = Math.random() > 0.5;
      const adminApproved = Math.random() > 0.5;
      
      clearanceSummary = {
        ITClearance: itApproved,
        HRClearance: hrApproved,
        FinanceClearance: financeApproved,
        AdminClearance: adminApproved,
        overallApproved: false,
        approvers: [
          {
            name: 'IT Manager',
            role: 'IT',
            approved: itApproved,
            timestamp: itApproved ? new Date(Date.now() - Math.floor(Math.random() * 3 * 24 * 60 * 60 * 1000)).toISOString() : undefined
          },
          {
            name: 'HR Director',
            role: 'HR',
            approved: hrApproved,
            timestamp: hrApproved ? new Date(Date.now() - Math.floor(Math.random() * 2 * 24 * 60 * 60 * 1000)).toISOString() : undefined
          },
          {
            name: 'Finance Manager',
            role: 'Finance',
            approved: financeApproved,
            timestamp: financeApproved ? new Date(Date.now() - Math.floor(Math.random() * 1 * 24 * 60 * 60 * 1000)).toISOString() : undefined
          },
          {
            name: 'Admin Officer',
            role: 'Admin',
            approved: adminApproved,
            timestamp: adminApproved ? new Date().toISOString() : undefined
          }
        ]
      };
    }
    
    records.push({
      id: `${i}`,
      employeeId: `EMP${(10000 + i).toString().padStart(5, '0')}`,
      name: `Employee ${i}`,
      email: `employee${i}@bytesplash.edu`,
      department,
      designation,
      exitDate: exitDate.toISOString().split('T')[0],
      status,
      reason,
      items,
      clearanceSummary,
      submittedBy: status !== 'pending' ? 'Manager User' : undefined,
      submittedDate: status !== 'pending' ? new Date(Date.now() - Math.floor(Math.random() * 10 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0] : undefined,
      completedDate: status === 'completed' ? new Date().toISOString().split('T')[0] : undefined
    });
  }
  
  return records;
};

const OFFBOARDING_DATA = generateOffboardingData();

const OffboardingChecklist = ({ 
  record, 
  onUpdate,
  isSubmittable = false,
  onSubmitForApproval,
  userRole = 'manager'
}: { 
  record: OffboardingRecord, 
  onUpdate: (id: string, items: OffboardingItem[]) => void,
  isSubmittable?: boolean,
  onSubmitForApproval?: (id: string) => void,
  userRole?: string
}) => {
  const [localItems, setLocalItems] = useState<OffboardingItem[]>([...record.items]);
  const fileInputRefs = useRef<{[key: string]: HTMLInputElement | null}>({});
  const { toast } = useToast();
  
  const groupedItems: Record<string, OffboardingItem[]> = {};
  localItems.forEach(item => {
    if (!groupedItems[item.category]) {
      groupedItems[item.category] = [];
    }
    groupedItems[item.category].push(item);
  });
  
  const handleToggleItem = (itemId: string) => {
    const updatedItems = localItems.map(item => 
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );
    
    setLocalItems(updatedItems);
    onUpdate(record.id, updatedItems);
  };
  
  const handleFileChange = (itemId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const updatedItems = localItems.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          completed: true,
          document: {
            fileName: file.name,
            uploadDate: new Date().toISOString().split('T')[0],
            status: 'pending' as DocumentStatus
          }
        };
      }
      return item;
    });
    
    setLocalItems(updatedItems);
    onUpdate(record.id, updatedItems);
    
    toast({
      title: "Document Uploaded",
      description: `${file.name} has been uploaded and is pending approval.`
    });
  };
  
  const handleDeleteDocument = (itemId: string) => {
    const updatedItems = localItems.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          completed: false,
          document: null
        };
      }
      return item;
    });
    
    setLocalItems(updatedItems);
    onUpdate(record.id, updatedItems);
    
    toast({
      title: "Document Removed",
      description: "The document has been removed."
    });
  };
  
  const triggerFileInput = (itemId: string) => {
    fileInputRefs.current[itemId]?.click();
  };
  
  const handleApproveDocument = (itemId: string) => {
    const updatedItems = localItems.map(item => {
      if (item.id === itemId && item.document) {
        return {
          ...item,
          document: {
            ...item.document,
            status: 'approved' as DocumentStatus,
            approvalDate: new Date().toISOString().split('T')[0],
            approvedBy: userRole.toUpperCase()
          }
        };
      }
      return item;
    });
    
    setLocalItems(updatedItems);
    onUpdate(record.id, updatedItems);
    
    toast({
      title: "Document Approved",
      description: "The document has been approved successfully."
    });
  };
  
  const handleRejectDocument = (itemId: string, comment: string = 'Document does not meet requirements') => {
    const updatedItems = localItems.map(item => {
      if (item.id === itemId && item.document) {
        return {
          ...item,
          document: {
            ...item.document,
            status: 'rejected' as DocumentStatus,
            comment
          }
        };
      }
      return item;
    });
    
    setLocalItems(updatedItems);
    onUpdate(record.id, updatedItems);
    
    toast({
      title: "Document Rejected",
      description: "The document has been rejected."
    });
  };
  
  const completedCount = localItems.filter(item => item.completed).length;
  const totalCount = localItems.length;
  const progress = Math.round((completedCount / totalCount) * 100);
  
  const canSubmitForApproval = isSubmittable && 
    completedCount > 0 &&
    localItems.filter(item => item.documentRequired && item.completed && (!item.document || item.document.status === 'rejected')).length === 0 &&
    record.status !== 'approval_pending' && 
    record.status !== 'completed';
  
  const categoryStatus: Record<string, boolean> = {};
  Object.entries(groupedItems).forEach(([category, items]) => {
    categoryStatus[category] = items.every(item => item.completed);
  });
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <div className="flex-1">
          <h3 className="text-sm font-medium">Offboarding Progress: {progress}%</h3>
          <div className="w-full h-2 bg-gray-100 rounded-full mt-1">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
              className="h-full bg-bytesplash-blue rounded-full"
            />
          </div>
        </div>
        <StatusBadge status={record.status as StatusType} />
      </div>
      
      {record.reason && (
        <div className="p-3 bg-gray-50 rounded-md mb-4">
          <div className="text-sm font-medium mb-1">Reason for Exit</div>
          <p className="text-gray-700">{record.reason}</p>
        </div>
      )}
      
      {record.clearanceSummary && (record.status === 'approval_pending' || record.status === 'completed') && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          <div className={`p-2 border rounded-md flex flex-col items-center text-center ${
            record.clearanceSummary.ITClearance ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <span className="text-xs font-medium">IT Clearance</span>
            {record.clearanceSummary.ITClearance ? 
              <CheckCircle className="h-5 w-5 text-green-500 mt-1" /> : 
              <Clock className="h-5 w-5 text-gray-400 mt-1" />}
          </div>
          
          <div className={`p-2 border rounded-md flex flex-col items-center text-center ${
            record.clearanceSummary.HRClearance ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <span className="text-xs font-medium">HR Clearance</span>
            {record.clearanceSummary.HRClearance ? 
              <CheckCircle className="h-5 w-5 text-green-500 mt-1" /> : 
              <Clock className="h-5 w-5 text-gray-400 mt-1" />}
          </div>
          
          <div className={`p-2 border rounded-md flex flex-col items-center text-center ${
            record.clearanceSummary.FinanceClearance ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <span className="text-xs font-medium">Finance Clearance</span>
            {record.clearanceSummary.FinanceClearance ? 
              <CheckCircle className="h-5 w-5 text-green-500 mt-1" /> : 
              <Clock className="h-5 w-5 text-gray-400 mt-1" />}
          </div>
          
          <div className={`p-2 border rounded-md flex flex-col items-center text-center ${
            record.clearanceSummary.AdminClearance ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <span className="text-xs font-medium">Admin Clearance</span>
            {record.clearanceSummary.AdminClearance ? 
              <CheckCircle className="h-5 w-5 text-green-500 mt-1" /> : 
              <Clock className="h-5 w-5 text-gray-400 mt-1" />}
          </div>
        </div>
      )}
      
      <div className="space-y-4">
        {Object.entries(groupedItems).map(([category, items]) => (
          <div key={category} className="space-y-2">
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-medium flex items-center">
                {category} Clearance
                {record.clearanceSummary && (
                  category === 'IT' && record.clearanceSummary.ITClearance ||
                  category === 'HR' && record.clearanceSummary.HRClearance ||
                  category === 'Finance' && record.clearanceSummary.FinanceClearance ||
                  category === 'Admin' && record.clearanceSummary.AdminClearance
                ) ? (
                  <CheckCircle className="h-4 w-4 ml-2 text-green-500" />
                ) : (
                  categoryStatus[category] && (
                    <Badge className="ml-2 bg-blue-100 text-blue-800 hover:bg-blue-100">
                      Ready for approval
                    </Badge>
                  )
                )}
              </h4>
            </div>
            
            <div className="space-y-1">
              {items.map(item => (
                <div 
                  key={item.id} 
                  className={`p-3 border rounded-md ${item.completed ? 'bg-gray-50' : 'bg-white'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div 
                        className={`flex-shrink-0 w-5 h-5 border rounded-sm cursor-pointer flex items-center justify-center ${
                          item.completed ? 'bg-bytesplash-blue border-bytesplash-blue text-white' : 'border-gray-300'
                        }`}
                        onClick={() => handleToggleItem(item.id)}
                      >
                        {item.completed && <CheckCircle className="w-4 h-4" />}
                      </div>
                      
                      <div className="flex-1">
                        <h5 className={`text-sm font-medium ${item.completed ? 'line-through text-gray-500' : ''}`}>
                          {item.name}
                          {item.documentRequired && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              Document Required
                            </Badge>
                          )}
                        </h5>
                        
                        {item.document && (
                          <div className="mt-2 p-2 bg-gray-100 rounded-md">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center">
                                <FileText className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                                <span className="text-xs text-gray-700">{item.document.fileName}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {userRole === 'manager' && item.document.status === 'pending' && (
                                  <>
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button 
                                            className="p-1 hover:bg-green-100 rounded-full"
                                            onClick={() => handleApproveDocument(item.id)}
                                          >
                                            <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="text-xs">Approve Document</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                    
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button 
                                            className="p-1 hover:bg-red-100 rounded-full"
                                            onClick={() => handleRejectDocument(item.id)}
                                          >
                                            <X className="h-3.5 w-3.5 text-red-600" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="text-xs">Reject Document</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </>
                                )}
                                
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button 
                                        className="p-1 hover:bg-red-100 rounded-full"
                                        onClick={() => handleDeleteDocument(item.id)}
                                      >
                                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="text-xs">Delete Document</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <div className="text-[10px] text-gray-500">
                                Uploaded: {item.document.uploadDate}
                              </div>
                              <div>
                                {item.document.status === 'approved' && (
                                  <Badge variant="success" className="text-[10px] px-1.5 py-0">Approved</Badge>
                                )}
                                {item.document.status === 'pending' && (
                                  <Badge variant="warning" className="text-[10px] px-1.5 py-0">Pending</Badge>
                                )}
                                {item.document.status === 'rejected' && (
                                  <Badge variant="rejected" className="text-[10px] px-1.5 py-0">Rejected</Badge>
                                )}
                              </div>
                            </div>
                            
                            {item.document.status === 'rejected' && item.document.comment && (
                              <div className="mt-1 text-[10px] text-red-600 bg-red-50 p-1 rounded">
                                {item.document.comment}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {item.documentRequired && !item.document && (
                      <div>
                        <input 
                          type="file" 
                          className="hidden" 
                          ref={el => fileInputRefs.current[item.id] = el}
                          onChange={(e) => handleFileChange(item.id, e)}
                        />
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-7 text-xs"
                          onClick={() => triggerFileInput(item.id)}
                        >
                          <Upload className="h-3 w-3 mr-1" />
                          Upload
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {canSubmitForApproval && onSubmitForApproval && (
        <div className="mt-6">
          <Button 
            className="w-full"
            onClick={() => onSubmitForApproval(record.id)}
          >
            <UserCheck className="h-4 w-4 mr-2" />
            Submit for Approval
          </Button>
        </div>
      )}
    </div>
  );
};

const EmployeeOffboarding = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedRecord, setSelectedRecord] = useState<OffboardingRecord | null>(null);
  const [records, setRecords] = useState<OffboardingRecord[]>(OFFBOARDING_DATA);
  const [viewingDetails, setViewingDetails] = useState(false);
  const { toast } = useToast();
  
  const filteredRecords = records.filter(record => {
    const matchesSearch = 
      record.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.employeeId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      record.department.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || record.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  const handleUpdateItems = (recordId: string, items: OffboardingItem[]) => {
    const updatedRecords = records.map(record => 
      record.id === recordId ? { ...record, items } : record
    );
    
    setRecords(updatedRecords);
    
    if (selectedRecord && selectedRecord.id === recordId) {
      setSelectedRecord({ ...selectedRecord, items });
    }
  };
  
  const handleSubmitForApproval = (recordId: string) => {
    const updatedRecords = records.map(record => {
      if (record.id === recordId) {
        const clearanceSummary: ClearanceSummary = {
          ITClearance: false,
          HRClearance: false,
          FinanceClearance: false,
          AdminClearance: false,
          overallApproved: false,
          approvers: [
            { name: 'IT Manager', role: 'IT', approved: false },
            { name: 'HR Director', role: 'HR', approved: false },
            { name: 'Finance Manager', role: 'Finance', approved: false },
            { name: 'Admin Officer', role: 'Admin', approved: false }
          ]
        };
        
        return {
          ...record,
          status: 'approval_pending' as OffboardingStatus,
          clearanceSummary,
          submittedBy: 'CURRENT USER',
          submittedDate: new Date().toISOString().split('T')[0]
        };
      }
      return record;
    });
    
    setRecords(updatedRecords);
    
    if (selectedRecord && selectedRecord.id === recordId) {
      const updatedRecord = updatedRecords.find(r => r.id === recordId);
      if (updatedRecord) {
        setSelectedRecord(updatedRecord);
      }
    }
    
    toast({
      title: "Submitted for Approval",
      description: "The offboarding checklist has been submitted for departmental approval."
    });
  };
  
  const handleRecordSelect = (record: OffboardingRecord) => {
    setSelectedRecord(record);
    setViewingDetails(true);
  };
  
  const handleApproveOffboarding = (departmentRole: DepartmentType) => {
    if (!selectedRecord) return;
    
    const updatedRecords = records.map(record => {
      if (record.id === selectedRecord.id && record.clearanceSummary) {
        const updatedClearanceSummary = { ...record.clearanceSummary };
        
        // Update the specific department clearance
        if (departmentRole === 'IT') updatedClearanceSummary.ITClearance = true;
        if (departmentRole === 'HR') updatedClearanceSummary.HRClearance = true;
        if (departmentRole === 'Finance') updatedClearanceSummary.FinanceClearance = true;
        if (departmentRole === 'Admin') updatedClearanceSummary.AdminClearance = true;
        
        // Update approver
        updatedClearanceSummary.approvers = updatedClearanceSummary.approvers.map(approver => {
          if (approver.role === departmentRole) {
            return {
              ...approver,
              approved: true,
              timestamp: new Date().toISOString()
            };
          }
          return approver;
        });
        
        // Check if all departments approved
        const allApproved = 
          updatedClearanceSummary.ITClearance && 
          updatedClearanceSummary.HRClearance && 
          updatedClearanceSummary.FinanceClearance && 
          updatedClearanceSummary.AdminClearance;
        
        updatedClearanceSummary.overallApproved = allApproved;
        
        return {
          ...record,
          status: allApproved ? 'completed' as OffboardingStatus : record.status,
          clearanceSummary: updatedClearanceSummary,
          completedDate: allApproved ? new Date().toISOString().split('T')[0] : record.completedDate
        };
      }
      return record;
    });
    
    setRecords(updatedRecords);
    
    const updatedRecord = updatedRecords.find(r => r.id === selectedRecord.id);
    if (updatedRecord) {
      setSelectedRecord(updatedRecord);
    }
    
    toast({
      title: `${departmentRole} Clearance Approved`,
      description: `The ${departmentRole} clearance has been approved for this employee.`
    });
  };
  
  const handleCloseDetails = () => {
    setViewingDetails(false);
    setSelectedRecord(null);
  };
  
  const handleViewApprovalDetails = (record: OffboardingRecord) => {
    setSelectedRecord(record);
    setViewingDetails(true);
  };
  
  return (
    <div className="container mx-auto py-6 px-4 max-w-7xl">
      <Header title="Employee Offboarding" />
      
      <Tabs defaultValue="all" className="mt-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <TabsList className="mb-4 md:mb-0">
            <TabsTrigger value="all">All Offboarding</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="in-progress">In Progress</TabsTrigger>
            <TabsTrigger value="approval_pending">Awaiting Approval</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
          </TabsList>
          
          <div className="flex gap-2 w-full md:w-auto">
            <Button variant="outline" className="text-sm" onClick={() => {}}>
              <FilePlus className="h-4 w-4 mr-2" />
              New Offboarding
            </Button>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search by name, ID or department..."
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <Select defaultValue="all" onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in-progress">In Progress</SelectItem>
              <SelectItem value="approval_pending">Awaiting Approval</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <TabsContent value="all" className="mt-0">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Exit Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.length > 0 ? (
                    filteredRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{record.employeeId}</TableCell>
                        <TableCell>{record.name}</TableCell>
                        <TableCell>{record.department}</TableCell>
                        <TableCell>{record.designation}</TableCell>
                        <TableCell>{record.exitDate}</TableCell>
                        <TableCell>
                          <StatusBadge status={record.status as StatusType} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8"
                            onClick={() => handleRecordSelect(record)}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-6 text-gray-500">
                        No offboarding records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="pending" className="mt-0">
          {/* Similar content as "all" but filtered for pending status */}
        </TabsContent>
        
        <TabsContent value="in-progress" className="mt-0">
          {/* Similar content as "all" but filtered for in-progress status */}
        </TabsContent>
        
        <TabsContent value="approval_pending" className="mt-0">
          {/* Similar content as "all" but filtered for approval_pending status */}
        </TabsContent>
        
        <TabsContent value="completed" className="mt-0">
          {/* Similar content as "all" but filtered for completed status */}
        </TabsContent>
      </Tabs>
      
      <Pagination className="mt-4">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious href="#" />
          </PaginationItem>
          <PaginationItem>
            <PaginationLink href="#">1</PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink href="#" isActive>2</PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink href="#">3</PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationNext href="#" />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      
      <Dialog open={viewingDetails} onOpenChange={setViewingDetails}>
        <DialogContent className="max-w -2xl">
          <DialogHeader>
            <DialogTitle>Offboarding Details</DialogTitle>
            <DialogDescription>
              View and manage the offboarding process for this employee.
            </DialogDescription>
          </DialogHeader>
          
          {selectedRecord && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <div className="text-sm text-gray-500">Employee</div>
                  <div className="font-medium">{selectedRecord.name}</div>
                  <div className="text-sm">{selectedRecord.employeeId}</div>
                </div>
                
                <div className="space-y-1">
                  <div className="text-sm text-gray-500">Position</div>
                  <div className="font-medium">{selectedRecord.designation}</div>
                  <div className="text-sm">{selectedRecord.department}</div>
                </div>
                
                <div className="space-y-1">
                  <div className="text-sm text-gray-500">Exit Date</div>
                  <div className="font-medium">{selectedRecord.exitDate}</div>
                  {selectedRecord.completedDate && (
                    <div className="text-sm text-green-600">Completed: {selectedRecord.completedDate}</div>
                  )}
                </div>
              </div>
              
              <div className="border-t pt-4">
                <OffboardingChecklist 
                  record={selectedRecord} 
                  onUpdate={handleUpdateItems}
                  isSubmittable={selectedRecord.status === 'pending' || selectedRecord.status === 'in-progress'}
                  onSubmitForApproval={handleSubmitForApproval}
                />
              </div>
              
              {selectedRecord.status === 'approval_pending' && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium mb-3">Department Approvals</h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedRecord.clearanceSummary?.approvers.map((approver) => (
                      <div key={approver.role} className="p-3 border rounded-md bg-gray-50">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium text-sm">{approver.name}</div>
                            <div className="text-xs text-gray-500">{approver.role} Department</div>
                          </div>
                          {approver.approved ? (
                            <Badge variant="success">Approved</Badge>
                          ) : (
                            <Button 
                              size="sm" 
                              className="h-8"
                              onClick={() => handleApproveOffboarding(approver.role as DepartmentType)}
                            >
                              Approve
                            </Button>
                          )}
                        </div>
                        {approver.approved && approver.timestamp && (
                          <div className="text-xs text-gray-500 mt-1">
                            Approved on: {new Date(approver.timestamp).toLocaleString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDetails}>Close</Button>
            <Button
              disabled={!selectedRecord || !selectedRecord.clearanceSummary?.overallApproved}
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Certificate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeOffboarding;
